import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { AccountantCompaniesService } from '../company/accountant-companies.service';
import { environment } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const companiesService = inject(AccountantCompaniesService);

  if (isKeycloakRequest(req.url)) {
    return next(req);
  }

  // Token wychodzi tylko do naszego API.
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const authedReq = attachHeaders(req, authService, companiesService);

  return next(authedReq).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        return authService.refreshAccessToken().pipe(
          switchMap(() => next(attachHeaders(req, authService, companiesService))),
          catchError(() => {
            authService.logout();
            return throwError(() => err);
          }),
        );
      }
      return throwError(() => err);
    }),
  );
};

function isKeycloakRequest(url: string): boolean {
  return url.startsWith(environment.keycloak.url);
}

function attachHeaders(
  req: HttpRequest<unknown>,
  authService: AuthService,
  companiesService: AccountantCompaniesService,
): HttpRequest<unknown> {
  const token = authService.accessToken();
  const companyId = companiesService.selectedCompanyId();

  const setHeaders: Record<string, string> = {};
  if (token) setHeaders['Authorization'] = `Bearer ${token}`;
  if (companyId) setHeaders['company-id'] = companyId;

  if (Object.keys(setHeaders).length === 0) return req;

  return req.clone({ setHeaders });
}

// isApiRequest odpowiada na jedno pytanie: czy to nasze API.
//
// Interceptor doklejał nagłówek Authorization do KAŻDEGO żądania wychodzącego
// z aplikacji. Wystarczyło jedno wywołanie do obcej domeny — obrazek, mapa,
// zewnętrzne API, adres pochodzący z danych — żeby token dostępowy użytkownika
// trafił do cudzego serwera i pozwolił działać w jego imieniu. Token jedzie
// wyłącznie pod adres bramy.
function isApiRequest(url: string): boolean {
  if (url.startsWith('/')) {
    return true; // adres względny — ten sam origin co aplikacja
  }
  try {
    return new URL(url, document.baseURI).origin === new URL(environment.apiUrl, document.baseURI).origin;
  } catch {
    return false;
  }
}
